import React, { useMemo, useState, useCallback } from 'react';
import { fmt } from '../utils';

// ── Single editable group ─────────────────────────────────────────────────────

function MaterialGroup({ title, color, items, onCostChange }) {
  if (!items || items.length === 0) return null;
  const total = items.reduce((s, i) => s + (i.cost || 0), 0);

  return (
    <div className="card" style={{ marginBottom: 16, padding: 0, overflow: 'hidden' }}>
      {/* Group header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 16px', borderLeft: `4px solid ${color}`, background: 'var(--bg)' }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>{title}</span>
        {total > 0 && <span style={{ fontWeight: 700, color, fontSize: 13 }}>{fmt(total)}</span>}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--bg)' }}>
            <th style={{ textAlign: 'left',  padding: '6px 16px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Material</th>
            <th style={{ textAlign: 'right', padding: '6px 12px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Qty</th>
            <th style={{ textAlign: 'right', padding: '6px 16px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Est. Cost</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--bg)' }}>
              <td style={{ padding: '7px 16px' }}>{item.label}</td>
              <td style={{ padding: '7px 12px', textAlign: 'right', color: 'var(--text-muted)', fontSize: 12 }}>
                {item.qty}
              </td>
              <td style={{ padding: '5px 16px 5px 8px', textAlign: 'right' }}>
                {item.cost != null ? (
                  <input
                    type="number"
                    defaultValue={item.cost}
                    onBlur={e => {
                      const v = e.target.value === '' ? null : Math.round(parseFloat(e.target.value));
                      if (v !== item.cost) onCostChange(item.label, v);
                    }}
                    style={{
                      width: 100, textAlign: 'right', fontWeight: 600,
                      fontSize: 13, padding: '3px 6px',
                      border: '1px solid transparent', borderRadius: 4,
                      background: 'transparent', color: 'var(--text)',
                    }}
                    onFocus={e => { e.target.style.border = '1px solid var(--brand)'; e.target.style.background = '#fff'; }}
                    onBlurCapture={e => { e.target.style.border = '1px solid transparent'; e.target.style.background = 'transparent'; }}
                  />
                ) : (
                  <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: 12 }}>get bid</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        {/* Group total row */}
        <tfoot>
          <tr style={{ borderTop: '1px solid var(--border)', background: 'var(--bg)' }}>
            <td colSpan={2} style={{ padding: '6px 16px', fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>
              Group Total
            </td>
            <td style={{ padding: '6px 16px', textAlign: 'right', fontWeight: 700, color }}>
              {total > 0 ? fmt(total) : '—'}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────

export default function MaterialsSection({ deal }) {
  const baseMaterials = useMemo(() => buildMaterialsList(deal), [deal]);

  // Overrides: label → cost (user-edited values, persisted in component state)
  const [overrides, setOverrides] = useState({});

  const handleCostChange = useCallback((label, cost) => {
    setOverrides(prev => ({ ...prev, [label]: cost }));
  }, []);

  // Merge overrides into materials
  const materials = useMemo(() => baseMaterials.map(group => ({
    ...group,
    items: group.items.map(item => ({
      ...item,
      cost: overrides[item.label] !== undefined ? overrides[item.label] : item.cost,
    })),
  })), [baseMaterials, overrides]);

  const allItems   = materials.flatMap(g => g.items);
  const grandTotal = allItems.reduce((s, i) => s + (i.cost || 0), 0);
  const needsBid   = allItems.filter(i => i.cost == null).length;

  return (
    <div>
      {/* Header bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: 'var(--brand)', color: '#fff', borderRadius: 'var(--radius)',
        padding: '12px 20px', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 2 }}>Materials List</div>
          <div style={{ fontWeight: 700, fontSize: 18 }}>{allItems.length} line items</div>
          {needsBid > 0 && (
            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
              {needsBid} item{needsBid > 1 ? 's' : ''} need a contractor bid
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 2 }}>Estimated Material Cost</div>
          <div style={{ fontWeight: 700, fontSize: 18 }}>{fmt(grandTotal)}</div>
        </div>
      </div>

      {/* Tap-to-edit hint */}
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 16,
        display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>✏️</span>
        <span>Tap any cost to edit — costs update totals instantly. Items showing <em>get bid</em> need contractor pricing.</span>
      </div>

      {materials.length === 0 && (
        <div className="empty-state">
          <div className="icon">📋</div>
          <div>Fill in the SOW and room details to generate a materials list.</div>
        </div>
      )}

      {materials.map((group, i) => (
        <MaterialGroup key={i} {...group} onCostChange={handleCostChange} />
      ))}

      <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', padding: '8px 0 16px' }}>
        Costs are estimates based on Jacksonville contractor rates. Tap any number to adjust.
      </div>
    </div>
  );
}

// ── Build materials list from deal data ───────────────────────────────────────

function buildMaterialsList(deal) {
  const groups = [];
  const sys   = deal.sow_systems   || {};
  const ext   = deal.sow_exterior  || {};
  const land  = deal.sow_landscape || {};
  const rooms = deal.rooms || [];

  // ── Flooring ─────────────────────────────────────────────────────────────
  const flooringByType = {};
  for (const room of rooms) {
    if (room.flooring_condition !== 'replace') continue;
    const sqft = room.sq_ft || (room.length_ft && room.width_ft ? room.length_ft * room.width_ft : 0);
    if (!sqft) continue;
    const type = room.flooring_type || 'LVP';
    flooringByType[type] = (flooringByType[type] || 0) + sqft;
  }
  const flooringItems = Object.entries(flooringByType).map(([type, sqft]) => {
    const rates = { LVP: 3.50, carpet: 2.50, tile: 5.00, hardwood: 8.00 };
    return {
      label: `${type} Flooring`,
      qty:   `${Math.ceil(sqft * 1.1)} sqft (incl. 10% waste)`,
      cost:  Math.round(sqft * (rates[type] || 3.50)),
    };
  });
  if (flooringItems.length) groups.push({ title: 'Flooring', color: '#e67e22', items: flooringItems });

  // ── Paint ─────────────────────────────────────────────────────────────────
  let totalWallSqft = 0, totalCeilingSqft = 0;
  for (const room of rooms) {
    const sqft = room.sq_ft || (room.length_ft && room.width_ft ? room.length_ft * room.width_ft : 0);
    if (room.paint_walls   && sqft) totalWallSqft    += Math.sqrt(sqft) * 4 * (room.ceiling_height || 8);
    if (room.paint_ceiling && sqft) totalCeilingSqft += sqft;
  }
  const paintItems = [];
  if (totalWallSqft    > 0) { const g = Math.ceil(totalWallSqft    / 400); paintItems.push({ label: 'Interior Wall Paint',  qty: `~${g} gal (${Math.round(totalWallSqft)} sqft)`,    cost: g * 45 }); }
  if (totalCeilingSqft > 0) { const g = Math.ceil(totalCeilingSqft / 400); paintItems.push({ label: 'Ceiling Paint',         qty: `~${g} gal (${Math.round(totalCeilingSqft)} sqft)`, cost: g * 35 }); }
  if (paintItems.length) groups.push({ title: 'Paint', color: '#f39c12', items: paintItems });

  // ── Electrical Fixtures ───────────────────────────────────────────────────
  let totalFixtures = 0, totalFans = 0;
  for (const room of rooms) { totalFixtures += room.light_fixtures || 0; totalFans += room.ceiling_fans || 0; }
  const elecItems = [];
  if (totalFixtures > 0) elecItems.push({ label: 'Light Fixtures',  qty: totalFixtures, cost: totalFixtures * 150 });
  if (totalFans     > 0) elecItems.push({ label: 'Ceiling Fans',    qty: totalFans,     cost: totalFans     * 200 });
  if (elecItems.length) groups.push({ title: 'Electrical Fixtures', color: '#f1c40f', items: elecItems });

  // ── Kitchen ───────────────────────────────────────────────────────────────
  const kitchenRooms = rooms.filter(r => r.room_type === 'kitchen');
  const kitchenItems = [];
  for (const r of kitchenRooms) {
    if (r.countertop_type && r.countertop_sq_ft) {
      const rates = { laminate: 20, granite: 65, quartz: 70, tile: 15, solid_surface: 45, butcher_block: 55, marble: 80, concrete: 65, other: 40 };
      const rate = rates[r.countertop_type] || 40;
      kitchenItems.push({ label: `${r.countertop_type.replace(/_/g,' ')} Countertop`, qty: `${r.countertop_sq_ft} sqft`, cost: Math.round(r.countertop_sq_ft * rate) });
    }
    if (r.sink_condition === 'replace')    kitchenItems.push({ label: 'Kitchen Sink',      qty: 1, cost: 200 });
    const apps = {
      appliance_fridge:      ['Refrigerator',     800],
      appliance_stove:       ['Stove / Range',     500],
      appliance_dishwasher:  ['Dishwasher',        400],
      appliance_microwave:   ['Microwave',         200],
      appliance_disposal:    ['Garbage Disposal',  150],
    };
    for (const [field, [label, cost]] of Object.entries(apps)) {
      if (r[field] === 'replace' || r[field] === 'new') kitchenItems.push({ label, qty: 1, cost });
    }
    if (r.cabinet_condition === 'replace' && r.cabinet_linear_ft) {
      kitchenItems.push({ label: 'Kitchen Cabinets', qty: `${r.cabinet_linear_ft} lft`, cost: Math.round(r.cabinet_linear_ft * 300) });
    }
  }
  if (kitchenItems.length) groups.push({ title: 'Kitchen', color: '#e74c3c', items: kitchenItems });

  // ── Bathroom ──────────────────────────────────────────────────────────────
  const bathRooms = rooms.filter(r => ['bathroom','master_bath'].includes(r.room_type));
  const bathItems = [];
  for (const r of bathRooms) {
    if (r.toilet_condition === 'replace')       bathItems.push({ label: `Toilet (${r.name})`,                         qty: 1, cost: 200 });
    if (r.vanity_condition === 'replace')       bathItems.push({ label: `Vanity — ${r.vanity_size || 'single'} (${r.name})`, qty: 1, cost: r.vanity_size === 'double' ? 900 : 600 });
    if (r.tub_shower_condition === 'replace')   bathItems.push({ label: `Tub/Shower Unit (${r.name})`,                qty: 1, cost: 1500 });
    if (r.tile_sq_ft > 0 && r.tub_shower_condition === 'retile') {
      bathItems.push({ label: `Shower Tile (${r.name})`, qty: `${r.tile_sq_ft} sqft`, cost: Math.round(r.tile_sq_ft * 3) });
    }
  }
  if (bathItems.length) groups.push({ title: 'Bathroom', color: '#2493b5', items: bathItems });

  // ── Roofing ───────────────────────────────────────────────────────────────
  if (sys.roof_action === 'replace' && sys.roof_sq_ft) {
    const squares = Math.ceil(sys.roof_sq_ft / 100);
    groups.push({ title: 'Roofing', color: '#7f8c8d', items: [
      { label: `Roofing Shingles — ${sys.roof_type || 'shingle'}`, qty: `${squares} squares`, cost: null },
      { label: 'Roofing Underlayment',                              qty: `${squares} squares`, cost: null },
      { label: 'Roofing Nails & Fasteners',                         qty: `${squares} squares`, cost: null },
    ]});
  }

  // ── HVAC ──────────────────────────────────────────────────────────────────
  if (sys.hvac_action === 'replace') {
    groups.push({ title: 'HVAC', color: '#16a085', items: [
      { label: `AC Condenser — ${sys.hvac_tons || '?'} ton`,      qty: 1, cost: null },
      { label: `Air Handler / Furnace — ${sys.hvac_tons || '?'} ton`, qty: 1, cost: null },
      { label: 'Refrigerant Lines & Fittings',                     qty: 1, cost: null },
    ]});
  }

  // ── Plumbing ──────────────────────────────────────────────────────────────
  if (sys.wh_action === 'replace') {
    groups.push({ title: 'Plumbing', color: '#2980b9', items: [
      { label: `Water Heater — ${sys.wh_type || 'electric'}`,     qty: 1, cost: sys.wh_type === 'tankless' ? 1200 : 600 },
    ]});
  }

  // ── Exterior ──────────────────────────────────────────────────────────────
  const extItems = [];
  if (ext.window_action && ext.window_action !== 'keep' && ext.window_count > 0) {
    const count = ext.window_action === 'replace_partial' ? Math.ceil(ext.window_count / 2) : ext.window_count;
    extItems.push({ label: `Windows — ${(ext.window_type || 'double pane').replace(/_/g,' ')}`, qty: `${count} windows`, cost: count * 350 });
  }
  if (ext.gutters_action === 'replace' && ext.gutters_linear_ft) {
    extItems.push({ label: 'Gutters', qty: `${ext.gutters_linear_ft} lft`, cost: Math.round(ext.gutters_linear_ft * 8) });
  }
  if (extItems.length) groups.push({ title: 'Exterior', color: '#27ae60', items: extItems });

  // ── Landscape & Hardscape ─────────────────────────────────────────────────
  const landItems = [];
  if (land.sod && land.sod_sq_ft) {
    landItems.push({ label: 'Sod', qty: `${land.sod_sq_ft} sqft`, cost: Math.round(land.sod_sq_ft * 0.45) });
  }
  if (land.fence_action && land.fence_action !== 'none' && land.fence_linear_ft) {
    const fenceRates = { wood: 25, chain_link: 15, vinyl: 30, iron: 40, other: 20 };
    landItems.push({ label: `${(land.fence_type || 'wood').replace(/_/g,' ')} Fence Panels`, qty: `${land.fence_linear_ft} lft`, cost: Math.round(land.fence_linear_ft * (fenceRates[land.fence_type] || 20)) });
  }
  if (landItems.length) groups.push({ title: 'Landscape & Hardscape', color: '#39b54a', items: landItems });

  return groups;
}
